import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import proxy from '../../api/axios';
import { formatDateTime } from '../../common/utils';
import ContestResult from '../ContestResult';

const ACCENT = '#137274';

const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

const STATUS_META = {
  Upcoming: { color: '#2563eb', label: 'Upcoming' },
  'In Progress': { color: '#1a7f37', label: 'Live' },
  Completed: { color: '#8b949e', label: 'Completed' },
  Unknown: { color: '#8b949e', label: 'Unknown' },
};

const RULES = [
  'Submit solutions before the contest deadline.',
  'Plagiarism results in immediate disqualification.',
  'Keep a stable internet connection while competing.',
  'The timer keeps running once you start — there is no pause.',
];

const FAQS = [
  {
    q: 'Can I pause the contest?',
    a: 'No. Once you start, the timer continues even if you leave and come back.',
  },
  {
    q: 'Will solutions be shared?',
    a: 'Yes — solutions are typically made available after the contest ends.',
  },
  {
    q: 'What if I lose connection mid-contest?',
    a: 'Rejoin from this page while time remains. Your progress depends on what you already submitted.',
  },
];

const CountdownCell = ({ value, label, isDark }) => (
  <Box
    sx={{
      minWidth: { xs: 58, sm: 72 },
      px: 1,
      py: 1.1,
      borderRadius: 2,
      textAlign: 'center',
      bgcolor: isDark ? alpha('#fff', 0.1) : alpha('#fff', 0.18),
      border: `1px solid ${isDark ? alpha('#fff', 0.14) : alpha('#fff', 0.3)}`,
      backdropFilter: 'blur(6px)',
    }}
  >
    <Typography
      sx={{
        fontWeight: 900,
        fontSize: { xs: '1.25rem', sm: '1.55rem' },
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.1,
        color: '#fff',
      }}
    >
      {pad(value)}
    </Typography>
    <Typography
      sx={{
        mt: 0.35,
        fontSize: '0.62rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: alpha('#fff', 0.78),
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const ContestInfoPage = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { contestId } = useParams();
  const navigate = useNavigate();

  const [contest, setContest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [joining, setJoining] = useState(false);

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)';

  useEffect(() => {
    const fetchContestDetails = async () => {
      try {
        const { data } = await proxy.get(`/contests/details/${contestId}`);
        setContest({
          ...data.contest,
          participationCount: data.participationCount,
          isUserParticipated: data.isUserParticipated,
          isTimeRemaining: data.isTimeRemaining,
        });
      } catch (error) {
        toast.error('Failed to fetch contest details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchContestDetails();
  }, [contestId]);

  useEffect(() => {
    if (!contest?.startTime) return undefined;
    const start = new Date(contest.startTime).getTime();
    if (Date.now() >= start) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [contest]);

  const countdown = useMemo(() => {
    if (!contest?.startTime) return null;
    const diff = new Date(contest.startTime).getTime() - now;
    if (diff <= 0) return { started: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      started: false,
      days: Math.floor(diff / (24 * 3600 * 1000)),
      hours: Math.floor((diff % (24 * 3600 * 1000)) / (3600 * 1000)),
      minutes: Math.floor((diff % (3600 * 1000)) / (60 * 1000)),
      seconds: Math.floor((diff % (60 * 1000)) / 1000),
    };
  }, [contest, now]);

  const isContestActive = (startTime, endTime) => {
    const t = Date.now();
    return new Date(startTime).getTime() <= t && new Date(endTime).getTime() >= t;
  };

  const isContestCompleted = (endTime) => new Date(endTime).getTime() < Date.now();

  const getContestStatus = (c) => {
    if (Date.now() < new Date(c.startTime).getTime()) return 'Upcoming';
    if (
      isContestActive(c.startTime, c.endTime) &&
      c.status !== 'Already Submitted' &&
      c.status !== 'Time Expired'
    ) {
      return 'In Progress';
    }
    if (
      isContestCompleted(c.endTime) ||
      c.status === 'Already Submitted' ||
      c.status === 'Time Expired'
    ) {
      return 'Completed';
    }
    return 'Unknown';
  };

  const confirmStart = async () => {
    if (!contest) return;
    setJoining(true);
    try {
      if (!contest.isUserParticipated) {
        await proxy.post(`/contests/join`, { id: contestId });
      }
      toast.success(
        `Contest ${contest.isUserParticipated ? 'resumed' : 'started'} successfully!`,
      );
      setConfirmOpen(false);
      navigate(`/contests/${contestId}/live`);
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to start the contest. Please try again.',
      );
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          background: pageBg,
        }}
      >
        <CircularProgress sx={{ color: ACCENT }} size={28} />
        <Typography sx={{ color: muted, fontWeight: 600 }}>Loading contest…</Typography>
      </Box>
    );
  }

  if (!contest) {
    return (
      <Box
        sx={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
          background: pageBg,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 480, borderRadius: 2 }}>
          Contest not found. Check the URL or return to the contests list.
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/contests')}
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            bgcolor: ACCENT,
            '&:hover': { bgcolor: '#0f5c5e' },
          }}
        >
          Back to contests
        </Button>
      </Box>
    );
  }

  const status = getContestStatus(contest);
  const meta = STATUS_META[status] || STATUS_META.Unknown;
  const start = formatDateTime(contest.startTime);
  const end = formatDateTime(contest.endTime);
  const canEnter =
    status === 'In Progress' &&
    (!contest.isUserParticipated || contest.isTimeRemaining);

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg }}>
      <Helmet>
        <title>{contest.title} | Contest | CrafterHack</title>
        <meta
          name="description"
          content={
            contest.description ||
            `Compete in ${contest.title} on CrafterHack — timed coding contest.`
          }
        />
      </Helmet>

      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          px: { xs: 2, sm: 3 },
          pt: { xs: 2.5, md: 3.5 },
          pb: { xs: 4, md: 5 },
          background: isDark
            ? `radial-gradient(900px 420px at 15% -20%, ${alpha(ACCENT, 0.45)}, transparent 55%), linear-gradient(135deg, #0f5c5e 0%, #137274 50%, #1a3335 100%)`
            : `linear-gradient(135deg, #0f5c5e 0%, #137274 45%, #2a9d9f 100%)`,
          color: '#fff',
        }}
      >
        <Box sx={{ maxWidth: 960, mx: 'auto' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/contests')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: alpha('#fff', 0.9),
              mb: 2,
              px: 0,
              '&:hover': { bgcolor: 'transparent', color: '#fff' },
            }}
          >
            All contests
          </Button>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Chip
                size="small"
                icon={<BoltOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                label={meta.label}
                sx={{
                  fontWeight: 800,
                  bgcolor: alpha('#fff', 0.16),
                  color: '#fff',
                  border: `1px solid ${alpha('#fff', 0.25)}`,
                  '& .MuiChip-icon': { color: '#fff' },
                }}
              />
              <Chip
                size="small"
                label={`${contest.duration} mins`}
                sx={{
                  fontWeight: 700,
                  bgcolor: alpha('#fff', 0.1),
                  color: alpha('#fff', 0.92),
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha('#fff', 0.14),
                  flexShrink: 0,
                  mt: 0.25,
                }}
              >
                <EmojiEventsOutlinedIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: '1.55rem', sm: '2rem' },
                    letterSpacing: '-0.035em',
                    lineHeight: 1.15,
                    mb: 0.75,
                  }}
                >
                  {contest.title}
                </Typography>
                <Typography sx={{ color: alpha('#fff', 0.85), maxWidth: 560, lineHeight: 1.5 }}>
                  Take on the challenge — timed problems, real pressure, ranked results.
                </Typography>
              </Box>
            </Box>

            {status === 'Upcoming' && countdown && !countdown.started && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    fontWeight: 800,
                    opacity: 0.85,
                    mb: 1.25,
                  }}
                >
                  Starts in
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <CountdownCell value={countdown.days} label="Days" isDark={isDark} />
                  <CountdownCell value={countdown.hours} label="Hours" isDark={isDark} />
                  <CountdownCell value={countdown.minutes} label="Mins" isDark={isDark} />
                  <CountdownCell value={countdown.seconds} label="Secs" isDark={isDark} />
                </Box>
              </Box>
            )}

            {status === 'Upcoming' && countdown?.started && (
              <Typography sx={{ mt: 2.5, fontWeight: 700 }}>
                Contest window is open — refresh if the live button doesn’t appear yet.
              </Typography>
            )}

            {canEnter && (
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowRoundedIcon />}
                onClick={() => setConfirmOpen(true)}
                sx={{
                  mt: 3,
                  textTransform: 'none',
                  fontWeight: 800,
                  px: 3,
                  py: 1.15,
                  borderRadius: 2,
                  bgcolor: '#fff',
                  color: ACCENT,
                  boxShadow: `0 10px 28px ${alpha('#000', 0.2)}`,
                  '&:hover': { bgcolor: alpha('#fff', 0.92) },
                }}
              >
                {contest.isTimeRemaining ? 'Resume contest' : 'Start contest now'}
              </Button>
            )}
          </motion.div>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, md: 4 } }}>
        {/* Stats */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: 1.5,
            mb: 3,
            mt: { xs: -1, md: -2 },
          }}
        >
          {[
            {
              icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />,
              label: 'Starts',
              value: `${start.date}`,
              sub: start.time,
            },
            {
              icon: <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />,
              label: 'Ends',
              value: `${end.date}`,
              sub: end.time,
            },
            {
              icon: <TimerOutlinedIcon sx={{ fontSize: 20 }} />,
              label: 'Duration',
              value: `${contest.duration}`,
              sub: 'minutes',
            },
            {
              icon: <GroupsOutlinedIcon sx={{ fontSize: 20 }} />,
              label: 'Participants',
              value: contest.participationCount ?? 0,
              sub: 'joined',
            },
          ].map((s) => (
            <Box
              key={s.label}
              sx={{
                p: 2,
                borderRadius: 2.5,
                border: `1px solid ${border}`,
                bgcolor: surface,
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  display: 'grid',
                  placeItems: 'center',
                  mb: 1,
                  bgcolor: alpha(ACCENT, isDark ? 0.2 : 0.1),
                  color: ACCENT,
                }}
              >
                {s.icon}
              </Box>
              <Typography variant="caption" sx={{ color: muted, fontWeight: 700 }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', letterSpacing: '-0.02em' }}>
                {s.value}
              </Typography>
              <Typography variant="caption" sx={{ color: muted }}>
                {s.sub}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          {/* Description */}
          <Box
            sx={{
              p: { xs: 2.25, sm: 2.75 },
              borderRadius: 2.5,
              border: `1px solid ${border}`,
              bgcolor: surface,
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', mb: 1.25 }}>
              About this contest
            </Typography>
            <Typography variant="body2" sx={{ color: muted, lineHeight: 1.65 }}>
              {contest.description ||
                'This contest challenges your skills in problem-solving, algorithm design, and efficiency under time pressure.'}
            </Typography>

            {status === 'Completed' && (
              <Alert
                severity="info"
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.06),
                  color: 'inherit',
                  '& .MuiAlert-icon': { color: ACCENT },
                }}
              >
                This contest has ended. Scroll down for results and rankings.
              </Alert>
            )}

            {canEnter && (
              <Button
                fullWidth
                variant="contained"
                startIcon={<PlayArrowRoundedIcon />}
                onClick={() => setConfirmOpen(true)}
                sx={{
                  mt: 2.5,
                  display: { xs: 'flex', md: 'none' },
                  textTransform: 'none',
                  fontWeight: 800,
                  bgcolor: ACCENT,
                  '&:hover': { bgcolor: '#0f5c5e' },
                }}
              >
                {contest.isTimeRemaining ? 'Resume contest' : 'Start contest now'}
              </Button>
            )}
          </Box>

          {/* Rules */}
          <Box
            sx={{
              p: { xs: 2.25, sm: 2.75 },
              borderRadius: 2.5,
              border: `1px solid ${border}`,
              bgcolor: surface,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <GavelOutlinedIcon sx={{ fontSize: 20, color: ACCENT }} />
              <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>Rules</Typography>
            </Box>
            <Box component="ul" sx={{ m: 0, pl: 2.25, color: muted }}>
              {RULES.map((rule) => (
                <Typography
                  component="li"
                  key={rule}
                  variant="body2"
                  sx={{ mb: 1, lineHeight: 1.5, '&:last-child': { mb: 0 } }}
                >
                  {rule}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>

        {/* FAQs */}
        <Box
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${border}`,
            bgcolor: surface,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 2.5, pt: 2.25, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpOutlineOutlinedIcon sx={{ fontSize: 20, color: ACCENT }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>FAQs</Typography>
          </Box>
          {FAQS.map((faq, i) => (
            <Accordion
              key={faq.q}
              disableGutters
              elevation={0}
              sx={{
                bgcolor: 'transparent',
                '&:before': { display: 'none' },
                borderTop: i === 0 ? 'none' : `1px solid ${border}`,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ px: 2.5, '& .MuiAccordionSummary-content': { my: 1.25 } }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2 }}>
                <Typography variant="body2" sx={{ color: muted, lineHeight: 1.55 }}>
                  {faq.a}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {status === 'Completed' && (
          <Box
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2.5,
              border: `1px solid ${border}`,
              bgcolor: surface,
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', mb: 2, px: { xs: 0.5, sm: 0 } }}>
              Results
            </Typography>
            <ContestResult contestId={contestId} embedded />
          </Box>
        )}
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => !joining && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${border}`,
            bgcolor: surface,
            backgroundImage: 'none',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          {contest.isUserParticipated ? 'Resume contest?' : 'Start contest?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: muted, lineHeight: 1.55 }}>
            {contest.isUserParticipated
              ? `Resume “${contest.title}” where you left off? The timer may already be running.`
              : `You’re about to enter “${contest.title}”. The timer starts when you join.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={joining}
            sx={{ textTransform: 'none', fontWeight: 700, color: muted }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmStart}
            disabled={joining}
            startIcon={
              joining ? <CircularProgress size={16} color="inherit" /> : <PlayArrowRoundedIcon />
            }
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              bgcolor: ACCENT,
              '&:hover': { bgcolor: '#0f5c5e' },
            }}
          >
            {joining
              ? 'Joining…'
              : contest.isUserParticipated
                ? 'Resume'
                : 'Start now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContestInfoPage;
